<template>
  <div class="ws-phone-input ws-field">
    <ws-input
      ref="input"
      v-bind="$attrs"
      v-model="phoneNumber"
      :error="hasMessage"
      hide-bottom-space
      autocomplete="tel-national"
      :restrict="restrict"
      v-on="omit({...$listeners}, 'input')"
    >
      <!-- @blur="hideTooltip" -->
      <template v-slot:prepend>
        <ws-select
          v-if="prefix"
          ref="select"
          v-model="prefix"
          :multiple="false"
          :dark="$attrs['dark'] || false"
          class="ws-phone-prefix-select"
          popup-content-class="ws-phone-prefix-select-content"
          :options="optionsOrdered"
          option-value="code"
          option-label="id"
          option-description="description"
          transition-show="jump-down"
          transition-hide="jump-up"
          hide-bottom-space
          autocomplete="tel-country-code"
          tabindex="0"
        >
          <template v-slot:selected-item="scope">
            <ws-country-flag
              :key="prefix.id"
              :country="prefix.id"
              class="q-mr-sm"
            />
            {{ scope.opt.code }}
          </template>
          <template v-slot:option="scope">
            <q-separator v-if="scope.index === numOfSuggested" />
            <q-item
              v-bind="scope.itemProps"
              v-on="scope.itemEvents"
            >
              <q-item-section avatar>
                <ws-country-flag :key="scope.opt.id" :country="scope.opt.id" />
              </q-item-section>
              <q-item-section>
                <q-item-label>{{ scope.opt.code }}</q-item-label>
                <q-item-label>{{ scope.opt.country }}</q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </ws-select>
      </template>

      <template v-for="(_, slot) of $scopedSlots" v-slot:[slot]="scope">
        <slot :name="slot" v-bind="scope"></slot>
      </template>
    </ws-input>
  </div>
</template>

<script lang="ts">
import {
  QItem,
  QItemSection,
  QItemLabel,
  QSeparator,
  QField,
} from 'quasar';

import { debounce, omit } from 'lodash-es';
import { config } from '@odin/services';
import { WsInput } from './WsInput.vue';
import { WsCountryFlag } from '@odin/components-vue2';
import phonePrefixes from './phonePrefixes.json';
import { computed, defineComponent, type Ref, ref, watch } from 'vue';

interface PhonePrefix {
  id: string;
  country: string;
  code: string;
  label?: string;
  preferred?: true;
}

const preferredPrefixes = {
  HU: ['SK', 'CZ', 'SE'],
  SK: ['CZ', 'HU', 'SE'],
  CZ: ['SK', 'HU', 'SE'],
  SE: ['NO', 'DE', 'FI']
};

export default defineComponent({
  name: 'WsPhoneInput',
  components: {
    QItem,
    QItemSection,
    QItemLabel,
    QSeparator,
    WsCountryFlag,
    WsInput,
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    restrict: {
      type: String,
      default: '0-9 +',
    },
    optionalText: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['input'],
  // eslint-disable-next-line
  setup(props, vm) {
    const options: PhonePrefix[] = phonePrefixes.map(prefix => ({
      ...prefix,
      label: `${prefix.country} (${prefix.id})`.toLocaleLowerCase(),
    }));

    const prefix: Ref<PhonePrefix> = ref(options[0]);
    const phoneNumber: Ref<String> = ref(props.value);
    const numOfSuggested: Ref<number> = ref(0);
    const preferredOptions: PhonePrefix[] = [];

    const input = ref(null);
    const select = ref(null);

    let fullValue = '';
    let suggestedAdded = false;

    /***  Methods  ***/

    const findPrefixByCountry = (country: string) => {
      const id = country.toLowerCase();
      return options.find((prefix: PhonePrefix) => prefix.id === id);
    };

    const addPreferred = (countries: string[]) => {
      countries.forEach((country: string) => {
        const found = findPrefixByCountry(country);
        if (found) {
          preferredOptions.push(found);
        }
      });
    };

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const presetPrefixes = () => {
      if (config.phonePrefixes?.length) {
        const countries = config.phonePrefixes.map((country: string) => country.toUpperCase());

        if (!props.value) {
          prefix.value = findPrefixByCountry(countries[0]);
        }

        addPreferred(countries);
        numOfSuggested.value = countries.length;

      } else {
        const country = config.companyCountry;

        if (!props.value) {
          prefix.value = findPrefixByCountry(country) || options[0];
        }

        // add additional suggested prefixes for selected country
        const countries = (preferredPrefixes as Record<string, string[]>)[country] || [];

        // condition fixes multiple adding, ref: PNO-1350
        if (!suggestedAdded) {
          addPreferred(countries);
          // add preselected prefix at the top
          preferredOptions.unshift(prefix.value);
        }

        numOfSuggested.value = (countries && countries.length + 1) || 0;
        suggestedAdded = true;
      }
    };

    const getPrefixFromNumber = (value: string): PhonePrefix | null => {
      const foundIndex = options.findIndex((prefix: PhonePrefix) =>
        value.startsWith(prefix.code)
      );

      if (foundIndex > -1 && options[foundIndex]) {
        return options[foundIndex];
      }

      return null;
    };

    const _handleValue = debounce(() => {
      let newNumber: string | null;
      let newPrefix: PhonePrefix | null;

      if(phoneNumber.value) {
        // Prepare/clean up the phone number
        newNumber = phoneNumber.value
        // Replace double zeroes with a +
          .replace(/^00/, '+')
        // Remove spaces between numbers
          .replace(/(\s|\.)/g, '');

        // Set the prefix automatically if entered with the phone number
        if (newNumber.startsWith('+')) {
          newPrefix = getPrefixFromNumber(newNumber);

          if (newPrefix) {
            prefix.value = newPrefix;
            newNumber = newNumber.replace(newPrefix.code, '');
          }
        }
      }

      phoneNumber.value = newNumber;

      validate();
      (input.value as unknown as HTMLElement)?.focus();

      // Emit full phone number with prefix if it changed
      const newFullValue = phoneNumber.value ? prefix.value.code + phoneNumber.value : '';
      if (fullValue !== newFullValue) {
        fullValue = newFullValue;
        vm.emit('input', fullValue);
      }
    }, 100);


    /***  On created  ***/

    presetPrefixes();


    /***  Computed  ***/
    const optionsOrdered = computed(() => {
      return [...preferredOptions, ...options];
    });

    const messages = computed(() => (input.value as any)?.messages || []);

    const hasMessage = computed((): boolean => {
      return !!messages.value.length;
    });

    /***  Throttles  ***/

    const validate = debounce(async () => {
      return (input.value as unknown as QField)?.validate();
    });


    /***  Watchers  ***/

    // Set the prefix automatically if entered into the input
    watch(
      () => props.value,
      () => {
        if (props.value) {
          _handleValue();
        }
      },
      {immediate: true}
    );

    // New prefix
    watch(
      [phoneNumber, prefix],
      () => {
        _handleValue();
      }
    );


    return {
      // Refs
      input,
      select,

      // Variables
      prefix,
      numOfSuggested,

      // Methods
      omit,

      // Computed
      optionsOrdered: optionsOrdered.value as Array<any>,
      phoneNumber,
      hasMessage,
    };

  },
});
</script>

<style lang="scss" scoped>
.ws-phone-input {
  > .ws-input.q-input.q-field {
    .ws-country-flag > ::v-deep img {
      border-radius: $ws-border-radius-sm;
    }

    > ::v-deep .q-field__inner > .q-field__control {
      padding-left: 0;

      .ws-select > .ws-select-inner {
        &.q-select.q-field.q-field--outlined.q-field--focused {
          box-shadow: none;
        }

        > .q-field__inner > .q-field__control {
          padding-right: 0;

          &,
          &:before,
          &:after {
            border-right: none;
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
          }

          .q-field__append {
            padding-left: 0;
          }
        }
      }
    }

    // Fixes compact mode in Admin
    ::v-deep(.ws-select) {
      > .q-field > .q-field__inner > .q-field__control > .q-field__control-container > .q-field__native {
        padding-top: 7px;
        padding-right: 4px;
      }
    }
  }
}
</style>

<style lang="scss">
// portaled content out of component DOM
.ws-phone-prefix-select-content {
  .q-item__section.q-item__section--avatar {
    min-width: 0;

    .ws-country-flag img {
      border-radius: $ws-border-radius-sm;
    }
  }
}
</style>
